import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { first, last } from 'lodash';
import moment from 'moment';

import { feSidebarOpen } from 'actions/ui.actions';
import determineIfDisabledDrag from 'bl/scheduler/determineIfDisabledDrag.bl';
import determineIfDragDisablingFetch from 'bl/scheduler/determineIfDragDisablingFetch.bl';
import shouldUpdateDayColumn from 'bl/scheduler/shouldUpdateDayColumn.bl';
import timeInRangeOld from 'bl/scheduler/timeInRangeOld.bl';
import DndDraggable from 'components/dnd/DndDraggable.component';
import DndDroppable from 'components/dnd/DndDroppable.component';
import SP from 'constants/ui/sidePanel.const';
import { selectBooking } from 'selectors/booking.selectors';
import { selectOldFlagsHideMiniModal } from 'selectors/oldFlags.selectors';
import { selectRequestStatus } from 'selectors/request.selectors';
import { selectSchedulerAllowModal } from 'selectors/scheduler.selectors';
import { selectSidebarOpen, selectSidebarPanel, selectSidebarProcedureModalMounted } from 'selectors/sidebar.selectors';
import { selectTimeOffFormCategory, selectTimeOffNonce } from 'selectors/timeoff.selectors';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatLocalizedTime, formatYearToDayCompact } from 'utils/date/format.util';
import { nowAsMoment, nowAsTimestamp } from 'utils/date/now.util';
import zonedMoment from 'utils/date/zonedMoment.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseJson from 'utils/parsers/parseJson';
import { isTimeAfter, isTimeBefore, isTimeEqual } from 'utils/predicates/time/compare.util';
import dnd from 'utils/react/dnd.util';
import setStateAfterRender from 'utils/react/setStateAfterRender.util';
import randomString from 'utils/values/randomString.util';
import uniqueIndexGenerator from 'utils/values/uniqueIndexGenerator.util';

import { setType } from 'booking/actions';
import { createIfAllowed, setBookedByButton } from 'scheduler/actions';
import determineDropLocation from 'scheduler/components/determineDropLocation.util';
import ScheduleColumnHeader from 'scheduler/components/Header/ScheduleColumnHeader.component';
import NewEntryPlaceholder from 'scheduler/components/NewEntryPlaceholder';
import ScheduleEvent from 'scheduler/components/ScheduleEvent';
import ScheduleProcedure from 'scheduler/components/ScheduleProcedure';
import SchedulerCell from 'scheduler/components/SchedulerCell.component';
import { calculateOverlapping, isInWorkingRange } from 'scheduler/utils';

const L = logger('DayColumn');

const USE_ALLOW_MODAL = true;
const USE_DAY_VIEW = true;
const USE_EDITING = false;

const calculateProcedureWidth = (procedures) => {
  const firstProcedure = first(procedures);

  if (!firstProcedure) return '99%';

  const offset = firstProcedure.overlapStatus.depth * 8;
  return `${99 - offset}%`;
};

class DayColumn extends React.Component {
  static defaultProps = { bookingTime: null, clientName: '', visibilityWhenManyColumns: '' };

  constructor(props) {
    super(props);
    this.myRef = React.createRef();
    this.state = { dragOverTimeEl: null, isDragging: false };
  }

  // noinspection JSCheckFunctionSignatures
  shouldComponentUpdate(nextProps) {
    return shouldUpdateDayColumn({ prev: this.props, next: nextProps });
  }

  getStyle = (style, snapshot, procedureId) => {
    const { isDragging, isDropAnimating, draggingOver } = asObject(snapshot);
    const { transform } = asObject(style);

    const draggingOverTime = asObject(parseJson(draggingOver)).time;
    const formattedOverTime = formatLocalizedTime(draggingOverTime);

    const elBookingWindow = document.querySelector('.BookingWindow');
    const elOldDragOverTime = document.querySelector('#dragOverTime');
    const elDrag =
      typeof procedureId === 'number'
        ? document.querySelector(`.is-${procedureId} .cal-container`)
        : procedureId
          ? document.querySelector(`.${procedureId}`)
          : void 1;

    if (elOldDragOverTime && transform) {
      elOldDragOverTime.innerText = formattedOverTime;
    } else if (elOldDragOverTime && !transform) {
      elOldDragOverTime.remove();
    }

    const timeContainer = document.createElement('div');
    timeContainer.setAttribute('id', 'dragOverTime');

    const timeEl = document.createTextNode(formattedOverTime);
    timeContainer.appendChild(timeEl);

    if (transform && elDrag && !elOldDragOverTime) {
      elDrag.style.display = 'flex';
      elDrag.prepend(timeContainer);
      if (!elBookingWindow) setStateAfterRender(this, { dragOverTimeEl: timeContainer, isDragging });
    }

    const alreadyDraggedEl = document.querySelector('.move-notification');
    if (alreadyDraggedEl) return style;

    return isDropAnimating ? { ...style, transitionDuration: '0.001s' } : { ...style };
  };

  setStylistWeekView = (stylistId) => void this.props.setWeekView(this.props.selectedDate, stylistId);

  createBooking = (time) => {
    const { createIfAllowed, salonId, stylist } = this.props;
    L.debug('createBooking()', { time, salonId, stylist });

    const path = `/salon/${salonId}/day/${moment(time).format('YYYYMMDD')}/appointment/new`;
    createIfAllowed(stylist.id, time, path, 'appointment');
  };

  createNewTimeoff = (time) => {
    const { salonId, stylist, setType, feSidebarOpen } = this.props;
    L.debug('createNewTimeoff()', { time, salonId, stylist });

    setType('timeoff');

    feSidebarOpen({
      dateString: formatYearToDayCompact(nowAsTimestamp()),
      panel: SP.timeOff,
      salonId,
      stylist,
      stylistId: asObject(stylist).id,
      time,
    });
  };

  createTimeoff = (time) => {
    const { createIfAllowed, salonId, stylist, setType } = this.props;
    L.debug('createTimeoff()', { time, salonId, stylist });

    const path = `/salon/${salonId}/day/${moment(time).format('YYYYMMDD')}/appointment/new`;
    createIfAllowed(stylist.id, time, path, 'timeoff');
    setType('timeoff');
  };

  dropLocation = (touchX, touchY, mouseX, mouseY) => {
    const stylistId = asObject(this.props.stylist).id;
    const { rangeByHours } = this.props;

    const options = { touchX, touchY, mouseX, mouseY, rangeByHours, stylistId };
    const result = determineDropLocation(options);

    L.debug('dropLocation()', result, '<-', options);
    return result;
  };

  render() {
    const {
      addedServices,
      allowModal,
      booking,
      bookingTime,
      categoriesAndServices,
      clientName,
      createIfAllowed,
      currentActiveProcedure,
      events,
      filteredNoNewlyAddedProcedures,
      hideMiniModal,
      history,
      hoveredCell,
      markProcedures,
      newEntryIndicator,
      onCheckout,
      onDeleteEvent,
      onMoveColumn,
      openingHours,
      panel,
      isSidebarOpen,
      pickCurrentStylist,
      rangeByHours,
      salonId,
      salonTimezone: tz,
      setBookedByButton,
      showModal,
      stylist,
      stylists,
      timeOffCategory,
      timeOffNonce,
      updateProcedure,
      updateProcedureStatus,
      visibilityWhenManyColumns,
      isDragDisabledByFetch,
      isProcedureModalMounted,
      handleProfileImageClick,
    } = this.props;

    const { dragOverTimeEl, isDragging } = this.state;

    const {
      type: bookType,
      startTime: bookBgn,
      show_stylist: isBookStylistShown,
      endTime: bookEnd,
      repeat_rule: bookRepeatRule,
      allDay: isBookAllDay,
      currentTime: bookTime,
      currentStylistId,
    } = asObject(booking);

    const isTimeOffType = bookType === 'timeoff';
    const bookRecurrences = bookRepeatRule === 'once' ? '' : 'RRULE:FREQ=NONE';

    const style = {};
    const now = nowAsMoment();
    const draggableIndex = uniqueIndexGenerator(0);
    const firstHour = first(rangeByHours);
    const lastHour = last(rangeByHours);

    const allDayEvents = events
      .filter(
        ({ end_time: end, start_time: bgn }) =>
          isTimeBefore(zonedMoment(tz, bgn), firstHour) && isTimeAfter(zonedMoment(tz, end), firstHour),
      )
      .map((event) => ({
        event: { ...event },
        key: `event-${event.id || randomString.long()}-${event.start_time}`,
        currentTime: firstHour,
        salonId,
        onDeleteEvent,
      }));

    const stackOrder = {};

    const currentBookingProcedures = addedServices
      .filter((service) => service.deleted !== true)
      .map((service) => ({ ...service, dismissModal: true, editing: true }));

    const checkedProcedures = calculateOverlapping([...filteredNoNewlyAddedProcedures, ...currentBookingProcedures]);

    // stylistId is used by DayScheduler
    // SEE: src/scheduler/components/DayScheduler.jsx
    const { id } = asObject(stylist);

    const th = 'DayColumn__header--dnd-target';
    const tc = 'DayColumn__column--dnd-target';

    const isDragDisabledEditLess =
      isDragDisabledByFetch || determineIfDisabledDrag({ panel, modal: isProcedureModalMounted });

    return (
      <div data-bem="DayColumn" ref={this.myRef}>
        <div data-bem="DayColumn__container" style={style} className="on-move">
          <DndDroppable tag={th} droppableId={dnd.dropStylistColumn({ tag: th, stylist: { id } })}>
            {({ droppableProps, innerRef }) => (
              <div
                data-bem="DayColumn__column-header--el-target"
                data-stylist-id={id}
                id={`column-header-${id}`}
                className="col-sticky"
                {...droppableProps}
                ref={innerRef}
              >
                <ScheduleColumnHeader
                  hovered={false}
                  onMoveColumn={onMoveColumn}
                  setStylistWeekView={this.setStylistWeekView}
                  stylist={stylist}
                  handleProfileImageClick={handleProfileImageClick}
                />
              </div>
            )}
          </DndDroppable>

          <DndDroppable tag={tc} droppableId={dnd.dropCell({ tag: tc, stylist: { id } })}>
            {({ droppableProps, innerRef, placeholder }) => (
              <div
                data-bem="DayColumn__colum-contents--el-target"
                ref={innerRef}
                className="cell-column"
                style={{ width: '100%' }}
                {...droppableProps}
              >
                {placeholder}
                {isBookAllDay && currentStylistId === id && (
                  <ScheduleEvent
                    tag="DayColumn--01--full-day--current-stylist"
                    openingHours={openingHours}
                    dayView={USE_DAY_VIEW}
                    firstTime={firstHour}
                    lastTime={lastHour}
                    allowModal={USE_ALLOW_MODAL}
                    isBeingDragged={false}
                    event={{
                      client_id: 0,
                      end_time: lastHour,
                      feedback: null,
                      id: -6000,
                      local_id: 10,
                      name: '',
                      notes: '',
                      origin: 'salon_created',
                      price: 0,
                      recurrences: bookRecurrences,
                      start_time: firstHour,
                      salon_id: salonId,
                      status: 'time_off',
                      stylist_id: id,
                      show_stylist: isBookStylistShown,
                      time_off_category: timeOffCategory,
                    }}
                    onDeleteEvent={alwaysNoop}
                    salonId={salonId}
                  />
                )}
                {allDayEvents.map((props) => (
                  <ScheduleEvent
                    tag="DayColumn--02--full-day"
                    openingHours={openingHours}
                    dayView={USE_DAY_VIEW}
                    firstTime={firstHour}
                    lastTime={lastHour}
                    {...props}
                    allowModal={USE_ALLOW_MODAL}
                  />
                ))}

                {rangeByHours.map((currentTime, indexCurrentTime) => {
                  if (!stackOrder[currentTime]) {
                    stackOrder[currentTime] = 0;
                  }

                  const isCurrentTimeInRange = timeInRangeOld(now, currentTime, tz, 15);
                  const isBookTimeInRange = timeInRangeOld(bookingTime, currentTime, tz, 15);
                  const isNewEnabled = fromAnyToBool(
                    newEntryIndicator && bookingTime && isBookTimeInRange && !isTimeOffType,
                  );

                  const isNewTimeoffBlock = fromAnyToBool(
                    !isBookAllDay && isTimeOffType && isTimeEqual(bookBgn, currentTime) && currentStylistId === id,
                  );

                  const currentProcedures = checkedProcedures
                    .filter((p) => timeInRangeOld(p.procedure.start_time, currentTime, tz, 15))
                    .map((p) => {
                      const { start_time: start, id: procedureId, duration } = p.procedure;

                      const momentCurrentTime = moment(currentTime);
                      const startWithDuration = moment(start).add(moment.duration(duration));

                      while (isTimeBefore(momentCurrentTime, startWithDuration)) {
                        stackOrder[momentCurrentTime] = (stackOrder[momentCurrentTime] || 0) + 1;
                        momentCurrentTime.add(15, 'minutes');
                      }

                      return {
                        procedure: p.procedure,
                        overlapStatus: p.overlapStatus,
                        key: `procedure-${procedureId}`,
                        currentTime,
                        currentActiveProcedure,
                        stackOrder: stackOrder[currentTime],
                        salonId,
                        history,
                        onCheckout,
                        updateProcedureStatus,
                        updateProcedure,
                        markLinkedProcedures: markProcedures,
                      };
                    });

                  const currentEntriesWrapperClasses = cn({
                    'current-entries': true,
                    current_time: isCurrentTimeInRange,
                  });

                  const currentEntriesWrapperStyle = Object.freeze({
                    display: 'flex',
                    width: calculateProcedureWidth(currentProcedures),
                  });

                  const currentEvents = events
                    .filter((event) => timeInRangeOld(event.start_time, currentTime, tz, 15))
                    .map((event) => ({
                      event,
                      key: `event-${event.id || randomString.long()}-timestamp-${event.start_time}`,
                      salonTimezone: tz,
                      currentTime,
                      salonId,
                      onDeleteEvent,
                    }));

                  return (
                    <SchedulerCell
                      stylist={stylist}
                      droppable
                      time={currentTime}
                      isDragging={isDragging}
                      key={`cell-${currentTime.format('HH:mm')}-${id}-${indexCurrentTime}`}
                      isInWorkingHour={isInWorkingRange(currentTime, openingHours)}
                      onClick={() => {
                        let path = `/salon/${salonId}/day/${currentTime.format('YYYYMMDD')}/`;

                        if (hideMiniModal) {
                          path = `/salon/${salonId}/day/${currentTime.format('YYYYMMDD')}/appointment/new/`;
                        }

                        if (
                          (currentProcedures.length === 0 && currentEvents.length === 0) ||
                          (currentProcedures.length === 0 && currentEvents[0].event.status === 'patch_test')
                        ) {
                          createIfAllowed(id, currentTime, path);
                          setBookedByButton(false);
                        }
                      }}
                      hovered={
                        hoveredCell !== null &&
                        id === hoveredCell.stylistId &&
                        currentTime.toISOString() === hoveredCell.time
                      }
                      dragOverTimeEl={dragOverTimeEl}
                    >
                      <div
                        data-bem="DayColumn__current-entries-wrapper"
                        className={currentEntriesWrapperClasses}
                        style={currentEntriesWrapperStyle}
                      >
                        {isNewTimeoffBlock && (
                          <DndDraggable
                            tag="DayColumn__event--single--dnd-source"
                            draggableId={dnd.dragEvent({
                              event: {
                                client_id: null,
                                end_time: bookEnd,
                                feedback: null,
                                id: -6000,
                                local_id: 10,
                                name: '',
                                notes: '',
                                origin: 'salon_created',
                                price: 0,
                                recurrences: 'RRULE:FREQ=NONE',
                                start_time: bookBgn,
                              },
                            })}
                            key={bookTime}
                            index={draggableIndex()}
                            isDragDisabled={isDragDisabledEditLess}
                          >
                            {({ dragHandleProps, draggableProps, innerRef }, snapshot) => (
                              <div
                                data-bem="DayColumn__event--single--el-source"
                                ref={innerRef}
                                {...draggableProps}
                                {...dragHandleProps}
                                style={this.getStyle(draggableProps.style, snapshot)}
                              >
                                <ScheduleEvent
                                  tag="DayColumn--03--single"
                                  firstTime={bookBgn}
                                  lastTime={bookEnd}
                                  openingHours={openingHours}
                                  isBeingDragged={snapshot.isDragging}
                                  dayView={USE_DAY_VIEW}
                                  event={{
                                    client_id: 0,
                                    end_time: bookEnd,
                                    feedback: null,
                                    id: -6000,
                                    local_id: 10,
                                    name: '',
                                    notes: '',
                                    origin: 'salon_created',
                                    price: 0,
                                    recurrences: bookRecurrences,
                                    start_time: bookBgn,
                                    salon_id: salonId,
                                    status: 'time_off',
                                    stylist_id: id,
                                    show_stylist: isBookStylistShown,
                                    time_off_category: timeOffCategory,
                                  }}
                                  onDeleteEvent={alwaysNoop}
                                  allowModal={allowModal}
                                  salonId={salonId}
                                  nonce={timeOffNonce}
                                />
                              </div>
                            )}
                          </DndDraggable>
                        )}
                        {currentEvents.map((localProps) => {
                          const { event } = localProps;
                          const classKey = `event${asObject(event).id || ''}`;

                          return (
                            <DndDraggable
                              tag="DayColumn__event--all--dnd-source"
                              draggableId={dnd.dragEvent({ event: { ...event, local_id: void 1 } })}
                              key={localProps.key}
                              index={draggableIndex()}
                              isDragDisabled={isDragDisabledEditLess}
                            >
                              {({ dragHandleProps, draggableProps, innerRef }, snapshot) => (
                                <div
                                  data-bem="DayColumn__event--all--el-source"
                                  ref={innerRef}
                                  {...draggableProps}
                                  {...dragHandleProps}
                                  style={this.getStyle(draggableProps.style, snapshot, classKey)}
                                >
                                  <ScheduleEvent
                                    {...localProps}
                                    tag="DayColumn--04--all"
                                    onDeleteEvent={onDeleteEvent}
                                    firstTime={firstHour}
                                    lastTime={lastHour}
                                    openingHours={openingHours}
                                    isBeingDragged={snapshot.isDragging}
                                    allowModal={USE_ALLOW_MODAL}
                                    dayView={USE_DAY_VIEW}
                                    clientName={clientName}
                                    classKey={classKey}
                                  />
                                </div>
                              )}
                            </DndDraggable>
                          );
                        })}
                        {currentProcedures.map(
                          // this comment avoids ESLint+Prettier formatting issue
                          (localProps) => {
                            const { procedure, key } = localProps ?? {};
                            const { dismissModal, editing, id } = procedure ?? {};

                            const isDragDisabledEditFull =
                              isSidebarOpen ||
                              isDragDisabledEditLess ||
                              determineIfDisabledDrag({
                                panel,
                                modal: isProcedureModalMounted,
                                editing: USE_EDITING && editing,
                              });

                            return (
                              <DndDraggable
                                tag="DayColumn__procedure--dnd-source"
                                draggableId={dnd.dragProcedure({ procedure })}
                                isDragDisabled={isDragDisabledEditFull}
                                key={key}
                                index={draggableIndex()}
                              >
                                {({ dragHandleProps, draggableProps, innerRef }, snapshot) => (
                                  <div
                                    data-bem="DayColumn__procedure--el-source"
                                    ref={innerRef}
                                    {...draggableProps}
                                    {...dragHandleProps}
                                    style={this.getStyle(draggableProps.style, snapshot, id)}
                                  >
                                    <ScheduleProcedure
                                      {...localProps}
                                      visibilityWhenManyColumns={visibilityWhenManyColumns}
                                      isBeingDragged={snapshot.isDragging}
                                      markLinkedProcedures={markProcedures}
                                      categoriesAndServices={categoriesAndServices}
                                      stylists={stylists}
                                      allowModal={USE_ALLOW_MODAL && !dismissModal}
                                      dayView={USE_DAY_VIEW}
                                      showModal={showModal}
                                      currentActiveProcedure={currentActiveProcedure}
                                      isDragDisabledIndicator={isDragDisabledEditFull}
                                      hoveredCell={isProcedureModalMounted ? hoveredCell : void 1}
                                    />
                                  </div>
                                )}
                              </DndDraggable>
                            );
                          },
                        )}

                        {isNewEnabled && (
                          <DndDraggable
                            tag="DayColumn__placeholder--dnd-source"
                            draggableId={dnd.dragPlaceholder()}
                            key="placeholder"
                            index={draggableIndex()}
                            isDragDisabled
                          >
                            {({ dragHandleProps, draggableProps, innerRef }) => (
                              <div
                                data-bem="DayColumn__placeholder--el-source"
                                ref={innerRef}
                                {...draggableProps}
                                {...dragHandleProps}
                              >
                                <NewEntryPlaceholder
                                  createBooking={this.createBooking}
                                  createNewTimeoff={this.createNewTimeoff}
                                  createTimeoff={this.createTimeoff}
                                  schedulerTime={currentTime}
                                  miniModal
                                  pickCurrentStylist={pickCurrentStylist}
                                />
                              </div>
                            )}
                          </DndDraggable>
                        )}
                      </div>
                    </SchedulerCell>
                  );
                })}
              </div>
            )}
          </DndDroppable>
        </div>
      </div>
    );
  }
}

const DayColumnHoc = connect(
  (state) => ({
    allowModal: selectSchedulerAllowModal(state),
    booking: selectBooking(state),
    hideMiniModal: selectOldFlagsHideMiniModal(state),
    isProcedureModalMounted: selectSidebarProcedureModalMounted(state),
    isDragDisabledByFetch: determineIfDragDisablingFetch({ requestStatus: selectRequestStatus(state) }),
    panel: selectSidebarPanel(state),
    isSidebarOpen: selectSidebarOpen(state),
    timeOffCategory: selectTimeOffFormCategory(state),
    timeOffNonce: selectTimeOffNonce(state),
  }),
  mapActionsToDispatchToProps({
    createIfAllowed,
    setBookedByButton,
    setType,
    feSidebarOpen,
  }),
  null,
  { withRef: true },
)(DayColumn);

export default DayColumnHoc;
